// TeacherStudentSubmissions.js

// React page for teacher to manage student submissions (corporate)
// - Filter by: year, class, type_of_writing (school_id & teacher_id from session)
// - Table: per-student row with Download + Delete
// - Bulk actions: Download All (ZIP) + Delete All (by class/type prefix)
//
// Backend endpoints:
// 1) GET    /api/corporate/submissions/list
//    → params: school_id, teacher_id, year, student_class, type_of_writing
//    → returns: { status, submissions: [ { student_name, student_class, student_reg_no, uploaded_at, ... } ] }
//
// 2) GET    /api/corporate/submissions/download
//    → params: school_id, teacher_id, year, student_class, student_reg_no, type_of_writing
//    → returns: { status, downloads: { html_report:{url}, submitted_pdf:{url} } }
//
// 3) DELETE /api/corporate/submissions/delete
//    → body: { school_id, teacher_id, year, student_class, student_reg_no, type_of_writing }
//
// 4) GET    /api/corporate/submissions/download-all
//    → params: school_id, teacher_id, year, student_class, type_of_writing
//    → returns: ZIP stream
//
// 5) DELETE /api/corporate/submissions/reset
//    → body: { container_name, prefix }

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// ✅ Prototype dropdown options (later replace with Azure Table fetch)
const YEAR_OPTIONS = [
  { value: "2026", label: "2026" },
  { value: "2025", label: "2025" },
  { value: "2024", label: "2024" },
];

const TYPE_OF_WRITING_OPTIONS = [
  { value: "situational writing", label: "Situational Writing" },
  { value: "composition", label: "Continuous Writing" },
  { value: "oral_exam", label: "Oral Exam" },
];

function TeacherStudentSubmissions({ session }) {
  // -----------------------------
  // Auth context (from session)
  // -----------------------------
  const schoolId = session?.school_id || "";
  const teacherId = session?.teacher_id || "";
  const teacherEmail = session?.teacher_email || "";

  // -----------------------------
  // Filters
  // -----------------------------
  const [year, setYear] = useState("");
  const [classId, setClassId] = useState("");
  const [typeOfWriting, setTypeOfWriting] = useState("situational writing");

  // -----------------------------
  // Class dropdown (fetched)
  // -----------------------------
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState("");

  // -----------------------------
  // Submissions list
  // -----------------------------
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Client-side search (name / reg no)
  const [search, setSearch] = useState("");


  // -----------------------------
  // Defaults
  // -----------------------------
  useEffect(() => {
    if (!year) setYear(new Date().getFullYear().toString());
  }, [year]);

  // Format uploaded_at to more readable form (if exists)
  const formatDateTime = (iso) => {
    if (!iso) return "";

    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;

    return d.toLocaleString("en-SG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };


  // -----------------------------
  // GET: classes for dropdown
  // -----------------------------
  const fetchClasses = async () => {
    setClassesError("");
    setClasses([]);

    if (!schoolId || !teacherId || !year) return;

    setClassesLoading(true);
    try {
      const res = await axios.get(`/api/corporate/classes`, {
        params: { school_id: schoolId, teacher_id: teacherId, year },
        withCredentials: true,
      });

      if (res.data?.status === "ok") {
        setClasses(res.data.classes || []);
      } else {
        setClassesError(res.data?.message || "Failed to load classes.");
      }
    } catch (e) {
      setClassesError(e?.response?.data?.message || e.message || "Failed to load classes.");
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    setClassId("");
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, teacherId, year]);

  const selectionReady = useMemo(() => {
    return Boolean(schoolId && teacherId && year && classId && typeOfWriting);
  }, [schoolId, teacherId, year, classId, typeOfWriting]);

  const isOralExam = typeOfWriting === "oral_exam";

  // -----------------------------
  // GET: submissions list
  // -----------------------------
  const fetchSubmissions = async () => {
    setError("");
    setSubmissions([]);

    if (!selectionReady) return;

    setLoading(true);
    try {
      const res = await axios.get(
        isOralExam
          ? `/api/corporate/oral-submissions/list`
          : `/api/corporate/submissions/list`,
        {
          params: isOralExam
            ? {
                school_id: schoolId,
                teacher_id: teacherId,
                year,
                student_class: classId,
              }
            : {
                school_id: schoolId,
                teacher_id: teacherId,
                year,
                student_class: classId,
                type_of_writing: typeOfWriting,
              },
        withCredentials: true,
        }
      );

      if (res.data?.status === "ok" || res.data?.status === "success"){
        setSubmissions(res.data.submissions || []);
      } else {
        setError(res.data?.message || "Failed to load submissions.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, teacherId, year, classId, typeOfWriting, selectionReady, isOralExam]);

  // -----------------------------
  // Download helpers
  // -----------------------------
  const downloadAll = async () => {
    if (!selectionReady) return;

    const qs = new URLSearchParams({
      school_id: schoolId,
      teacher_id: teacherId,
      year,
      student_class: classId,
      type_of_writing: typeOfWriting,
    }).toString();

    window.open(`/api/corporate/submissions/download-all?${qs}`, "_blank");
  };

  const closeReservedTab = (tab) => {
    if (tab && !tab.closed) {
      tab.close();
    }
  };

  const navigateReservedTab = (tab, url) => {
    if (!url) {
      closeReservedTab(tab);
      return;
    }

    if (tab && !tab.closed) {
      tab.opener = null;
      tab.location.href = url;
      if (typeof tab.focus === "function") tab.focus();
      return;
    }

    window.location.href = url;
  };

  const downloadOne = async (studentRegNo, sessionId = "") => {
    const reportTab = window.open("about:blank", "_blank");
    const attachmentTab = isOralExam ? null : window.open("about:blank", "_blank");

    try {
      if (isOralExam && !sessionId) {
        closeReservedTab(reportTab);
        setError("Missing oral exam session ID for this report.");
        return;
      }

    const res = await axios.get(
      isOralExam
        ? `/api/corporate/oral-submissions/download`
        : `/api/corporate/submissions/download`,
      {
        params: isOralExam
          ? {
              school_id: schoolId,
              teacher_id: teacherId,
              year,
              student_class: classId,
              student_reg_no: studentRegNo,
              session_id: sessionId,
            }
          : {
              school_id: schoolId,
              teacher_id: teacherId,
              year,
              student_class: classId,
              student_reg_no: studentRegNo,
              type_of_writing: typeOfWriting,
            },
        withCredentials: true,
      }
      );

      if (res.data?.status !== "success") {
        closeReservedTab(reportTab);
        closeReservedTab(attachmentTab);
        setError(res.data?.message || "Failed to generate download links.");
        return;
      }

      if (isOralExam) {
        const oralHtmlUrl =
          res.data?.html_report?.url ||
          res.data?.downloads?.html_report?.url ||
          res.data?.downloads?.html_report_sas_url ||
          res.data?.html_report_sas_url ||
          res.data?.report_url ||
          "";

        if (oralHtmlUrl) {
          navigateReservedTab(reportTab, oralHtmlUrl);
        } else {
          closeReservedTab(reportTab);
          setError("No oral exam report found for this student.");
        }
        return;
      }

      const htmlUrl =
        res.data?.downloads?.html_report?.url ||
        res.data?.downloads?.html_report_sas_url ||
        res.data?.html_report?.url ||
        res.data?.html_report_sas_url ||
        res.data?.report_url ||
        "";
      const pdfUrl =
        res.data?.downloads?.submitted_pdf?.url ||
        res.data?.downloads?.submitted_pdf_sas_url ||
        res.data?.submitted_pdf?.url ||
        res.data?.submitted_pdf_sas_url ||
        "";

      navigateReservedTab(reportTab, htmlUrl || pdfUrl);
      navigateReservedTab(attachmentTab, htmlUrl && pdfUrl ? pdfUrl : "");

      if (!pdfUrl && !htmlUrl) {
        setError("No downloadable files found for this student.");
      }
    } catch (e) {
      closeReservedTab(reportTab);
      closeReservedTab(attachmentTab);
      setError("Failed to generate download links.");
    }
  };

  const downloadAiCheck = async (studentRegNo) => {
    if (isOralExam) {
      return;
    }

    try {
      const res = await axios.get(`/api/corporate/submissions/download-ai-check`, {
        params: {
          school_id: schoolId,
          teacher_id: teacherId,
          year,
          student_class: classId,
          student_reg_no: studentRegNo,
          type_of_writing: typeOfWriting,
        },
        withCredentials: true,
      });

      if (res.data?.status !== "success") {
        setError(res.data?.message || "Failed to generate AI-check link.");
        return;
      }

      const aiUrl = res.data?.downloads?.ai_check_html?.url;
      if (aiUrl) window.open(aiUrl, "_blank", "noopener,noreferrer");
      else setError("AI-check report not found for this student.");
    } catch (e) {
      setError("Failed to generate AI-check link.");
    }
  };

  // -----------------------------
  // Delete helpers
  // -----------------------------
  const deleteOne = async (studentRegNo) => {
  const ok = window.confirm("Delete this submission?");
  if (!ok) return;

  try {
    await axios.delete(`/api/corporate/submissions/delete`, {
      data: {
        school_id: schoolId,
        teacher_id: teacherId,
        year,
        student_class: classId,
        student_reg_no: studentRegNo,
        type_of_writing: typeOfWriting,
      },
      withCredentials: true,
    });
    fetchSubmissions();
  } catch (e) {
    setError(e?.response?.data?.message || e.message || "Delete failed.");
  }
};

  const deleteAll = async () => {
  const ok = window.confirm("Delete ALL submissions for this class/year/type?");
  if (!ok) return;

  // must match backend list_student_submissions() folder rules
  const gradedSubfolder =
    typeOfWriting === "composition" ? "compo-graded-reports" : "sit-writing-graded-reports";

  const prefix = `${schoolId}/${teacherId}/${year}/${classId}/${gradedSubfolder}/`;

  try {
    await axios.delete(`/api/corporate/submissions/reset`, {
      data: { container_name: "gradingdata", prefix },
      withCredentials: true,
    });
    fetchSubmissions();
  } catch (e) {
    setError(e?.response?.data?.message || e.message || "Delete failed.");
  }
};

  // -----------------------------
  // Client-side filter
  // -----------------------------
  const filteredSubmissions = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return submissions;

    return submissions.filter((s) => {
      const name = (s.student_name || "").toLowerCase();
      const reg  = (s.student_reg_no || s.student_reg_number || "").toLowerCase();
      return name.includes(q) || reg.includes(q);
    });
  }, [submissions, search]);

  const renderRiskBadge = (risk, submission) => {
    const level = (risk || "unknown").toLowerCase();

    if (level === "unknown") {
      return <span>—</span>;
    }

    const styles = {
      low: { bg: "#E6F6EC", color: "#137333", label: "Low" },
      medium: { bg: "#FFF4E5", color: "#B06000", label: "Review" },
      high: { bg: "#FDE8E8", color: "#B91C1C", label: "High" },
      unknown: { bg: "#F3F4F6", color: "#6B7280", label: "—" },
    };

    const s = styles[level] || styles.unknown;

    return (
      <span
        onClick={() => downloadAiCheck(submission.student_reg_no)}
        style={{
          background: s.bg,
          color: s.color,
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          display: "inline-block",
          cursor: "pointer",
        }}
        title="Open AI analysis report"
      >
        {s.label}
      </span>
    );
  };


  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div style={{ padding: 18, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "6px 0 12px" }}>Teacher Dashboard — Student Submissions</h2>

      {/* Session info (no input fields) */}
      <div style={{ marginBottom: 12, padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
        <div style={{ fontSize: 14, opacity: 0.85 }}>
          <strong>School:</strong> {schoolId || "-"} &nbsp;|&nbsp; <strong>Teacher:</strong> {teacherId || "-"}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>{teacherEmail ? `Signed in as ${teacherEmail}` : ""}</div>
      </div>

      {/* Filters */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(200px, 1fr))", gap: 12 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <label>Year *</label>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Select year</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label>Class *</label>
          <select
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            disabled={classesLoading || !schoolId || !teacherId}
          >
            <option value="">
              {classesLoading ? "Loading..." : classesError ? "Failed to load classes" : "Select class"}
            </option>
            {classes.map((c) => (
              <option key={c.student_class} value={c.student_class}>
                {c.student_class}
              </option>
            ))}
          </select>
          {classesError ? <div style={{ color: "crimson" }}>{classesError}</div> : null}
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <label>Type of Assignment *</label>
          <select value={typeOfWriting} onChange={(e) => setTypeOfWriting(e.target.value)}>
            {TYPE_OF_WRITING_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" onClick={fetchSubmissions} disabled={!selectionReady || loading}>
          Refresh
        </button>
        <button type="button" onClick={downloadAll} disabled={!selectionReady || isOralExam}>
          Download All (ZIP)
        </button>
        <button type="button" onClick={deleteAll} disabled={!selectionReady || isOralExam} style={{ backgroundColor: "crimson", color: "white" }}>
          Delete All
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12, opacity: 0.7 }}>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="name or reg no"
            style={{ padding: 8, minWidth: 220 }}
          />
        </div>

        {!selectionReady ? <span style={{ opacity: 0.7 }}>Select Year / Class / Type first.</span> : null}
      </div>

      {error ? <div style={{ marginTop: 12, color: "crimson" }}>{error}</div> : null}

      <div style={{ marginTop: 16 }}>
        {loading ? (
          <div>Loading...</div>
        ) : filteredSubmissions.length === 0 ? (
          <div style={{ opacity: 0.75 }}>No submissions found.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>Student</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>Class</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>Reg No</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>
                  {isOralExam ? "Topic" : "Uploaded"}
                </th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>
                  {isOralExam ? "Completed At" : "AI Use? (beta)"}
                </th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((s) => (
                <tr key={s.report_blob_path || s.blob_path || s.session_id || s.filename}>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>{s.student_name}</td>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>{s.student_class}</td>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>{s.student_reg_no || s.student_reg_number || "—"}</td>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>
                    {isOralExam ? (s.topic || s.topic_title || "—") : formatDateTime(s.uploaded_at || s.uploaded_at_sgt)}
                  </td>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>
                    {isOralExam
                      ? formatDateTime(s.completed_at || s.completed_at_sgt || s.uploaded_at || s.uploaded_at_sgt) || "—"
                      : renderRiskBadge(s.ai_risk_level, s)}
                  </td>
                  <td style={{ borderBottom: "1px solid #eee", padding: "8px 6px" }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        type="button"
                        onClick={() =>
                          downloadOne(
                            s.student_reg_no || s.student_reg_number,
                            s.session_id
                          )
                        }
                      >
                        {isOralExam ? "Download Report" : "Download"}
                      </button>
                      {!isOralExam ? (
                        <button type="button" onClick={() => deleteOne(s.student_reg_no)} style={{ backgroundColor: "crimson", color: "white" }}>
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default TeacherStudentSubmissions;
